import _ from 'underscore';
import SpriteHTML from '../../html/sprite.html';
import BaseView from '../baseView';
import ImgLoader from '../util/imgLoader';

const DEFAULTS = Object.freeze({
	spriteSheetUrl: undefined,
	class: '',
	frameWidth: 300,
	frameHeight: 300,
	spriteSheetWidth: 1200,
	spriteSheetHeight: 1800,
	delay: 0,
	duration: 1000,
	flicker: false,
	numLoops: 1,
	removeOnStop: true,
});

/**
 * @typedef {Partial<string, any>} SpriteAttributes
 * @property {string} [id] Unique id for sprite
 * @property {string} [spriteId] Alternate id for sprite (because reasons?)
 * @property {string} [spriteSheetUrl] Path to sprite sheet
 * @property {string} [class] Additional classes applied on this.el
 * @property {number} [frameWidth] Width of a frame in sprite sheet
 * @property {number} [frameHeight] Height of a frame in sprite sheet
 * @property {number} [spriteSheetWidth] Native width of sprite sheet
 * @property {number} [spriteSheetHeight] Native height of sprite sheet
 * @property {number} [delay] Delay before playing sprite in milliseconds
 * @property {number} [duration] Duration of Sprite in milliseconds
 * @property {boolean} [flicker] If true, duration is ignored
 * @property {number} [numLoops] Number of loops. Accepts numbers or Infinity
 * @property {boolean} [removeOnStop] Once sprite is over, to remove or not to remove
 */

export default class SpritePlayer extends BaseView {
	get CN() {
		return SpritePlayer.name;
	}

	className() {
		return 'sprite-player';
	}

	get template() {
		return _.template(SpriteHTML);
	}

	get canPlay() {
		return this.canPlayPromise;
	}

	initialize(options) {
		this.logger.log(`[${this.initialize.name}]`, { ...options });

		/** @type {string | undefined} Path to sprite sheet */
		this.spriteSheetUrl = DEFAULTS.spriteSheetUrl;
		/** @type {string} Additional classes applied on this.el */
		this.class = DEFAULTS.class;
		/** @type {number} Width of a frame in sprite sheet */
		this.frameWidth = DEFAULTS.frameWidth;
		/** @type {number} Height of a frame in sprite sheet */
		this.frameHeight = DEFAULTS.frameHeight;
		/** @type {number} Native width of sprite sheet */
		this.spriteSheetWidth = DEFAULTS.spriteSheetWidth;
		/** @type {number} Native height of sprite sheet */
		this.spriteSheetHeight = DEFAULTS.spriteSheetHeight;
		/** @type {number} Delay before playing sprite in milliseconds */
		this.delay = DEFAULTS.delay;
		/** @type {number} Duration of Sprite in milliseconds */
		this.duration = DEFAULTS.duration;
		/** @type {boolean} If true, duration is ignored */
		this.flicker = DEFAULTS.flicker;
		/** @type {number} Number of loops. Accepts numbers or Infinity */
		this.numLoops = DEFAULTS.numLoops;
		/** @type {boolean} Once sprite is over, to remove or not to remove */
		this.removeOnStop = DEFAULTS.removeOnStop;

		super.initialize(options);

		// Add any additional classes to help with specificity
		this.$el.addClass(this.class);

		// Ensure image has been loaded before trying to play a sprite
		this.canPlayPromise = new Promise((resolve, reject) => {
			this.onCanPlayResolve = resolve;
			this.onCanPlayReject = reject;
		});

		this.render();
	}

	render() {
		this.logger.log(`[${this.render.name}]`, { spriteSheetUrl: this.spriteSheetUrl });

		if (!this.spriteSheetUrl) {
			this.logger.error(`Sprite "${this.id}" has no spritShetUrl`);
			return Promise.reject(new Error(`Sprite "${this.id}" has no spritShetUrl`));
		}

		// Make sure sprite sheet is loaded before trying to play it.
		return ImgLoader.loadImage(this.spriteSheetUrl)
			.then((image) => {
				/** @type {HTMLImageElement} */
				this.image = image;

				super.render();

				/** @type {HTMLCanvasElement} */
				this.canvasElement = this.$('canvas').get(0);

				/** @type {CanvasRenderingContext2D} */
				this.canvasContext = this.canvasElement.getContext('2d');
			})
			.then(() => this.onCanPlayResolve(this))
			.catch((error) => this.onCanPlayResolve(error));
	}

	/**
	 * Plays sprite then resolves a promise to make chaining easier.
	 *
	 * @param {number} playDelay
	 * @returns {Promise}
	 */
	play(playDelay = this.delay) {
		this.logger.log(`[${this.play.name}]`, { playDelay });

		return this.canPlay
			.then(
				() => this.wait(playDelay),
				() => Promise.reject(new Error('Unable to play Sprite.')),
			)
			.then(
				() =>
					new Promise((resolve) => {
						this.numCols = this.spriteSheetWidth / this.frameWidth;
						this.numRows = this.spriteSheetHeight / this.frameHeight;
						this.currentRow = 0;
						this.currentColumn = 0;
						this.loopsCompleted = 0;

						this.timeBetweenFrames = this.duration / (this.numCols * this.numRows);

						if (this.numLoops === Infinity) {
							resolve();
						}

						// Start animation updates
						this.timeOfUpdate = 0;
						this.animInterval = window.requestAnimationFrame(
							this.animationStep.bind(this, resolve),
						);
					}),
			);
	}

	animationStep(onComplete) {
		this.logger.debug(`[${this.animationStep.name}]`);

		const { numCols, timeBetweenFrames, flicker } = this;

		// Check if it's been long enough to update the frame
		const timeDelta = Date.now() - this.timeOfUpdate;
		if (timeDelta < timeBetweenFrames) {
			this.animInterval = window.requestAnimationFrame(
				this.animationStep.bind(this, onComplete),
			);
			return;
		}

		// Update in accordance with timeBetweenFrames
		this.timeOfUpdate = Date.now();

		// For flicker, we override delay with a random value each loop
		if (flicker) {
			this.timeBetweenFrames = _.random(10, 1000);
		}

		// Update the frame
		this.canvasContext.clearRect(0, 0, this.canvasElement.width, this.canvasElement.height);
		this.canvasContext.drawImage(
			this.image,
			// * Where to start grabbing
			this.currentColumn * this.frameWidth,
			this.currentRow * this.frameHeight,
			// * How big of a grab
			this.frameWidth,
			this.frameHeight,
			// * Where to place grab
			0,
			0,
			// * Size we want it drawn
			this.canvasElement.width,
			this.canvasElement.height,
		);

		// Update our row/col
		if (this.currentColumn >= numCols - 1) {
			this.currentRow += 1;
			this.currentColumn = 0;
		} else {
			this.currentColumn += 1;
		}

		// update loop counting
		const currentFrame = this.currentRow * this.numCols + this.currentColumn;
		const totalFrames = this.numRows * this.numCols;
		if (currentFrame >= totalFrames) {
			this.loopsCompleted += 1;
			this.currentRow = 0;
			this.currentColumn = 0;
			this.timeOfUpdate = 0;
		}

		// check termination
		if (this.loopsCompleted >= this.numLoops) {
			this.stop();
			onComplete();
		} else {
			this.animInterval = window.requestAnimationFrame(
				this.animationStep.bind(this, onComplete),
			);
		}
	}

	stop() {
		if (this.animInterval) {
			window.cancelAnimationFrame(this.animInterval);
			this.animInterval = 0;

			if (this.removeOnStop && this.numLoops !== Infinity) {
				this.$el.remove();
			}
		}
	}
}
